/**
 * Variant Gallery Swap
 * Swaps product gallery images when color variant changes
 * Like Alo Yoga does!
 */

class VariantGallerySwap {
  constructor(productId) {
    this.productId = productId;
    this.dataElement = document.getElementById(`variant-images-data-${productId}`);
    
    if (!this.dataElement) {
      console.warn('VariantGallerySwap: No data element found for product', productId);
      return;
    }

    try {
      this.data = JSON.parse(this.dataElement.textContent);
    } catch (e) {
      console.error('VariantGallerySwap: Failed to parse data', e);
      return;
    }

    this.gallery = document.querySelector('media-gallery');
    this.currentVariantId = null;
    
    this.init();
  }

  init() {
    // Listen for variant changes
    document.addEventListener('variant:change', (e) => this.handleVariantChange(e));
    
    // Also listen for the custom event from Horizon theme
    document.addEventListener('variant-selected', (e) => this.handleVariantChange(e));
    
    // Listen for radio button changes in variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('change', (e) => {
        if (e.target.type === 'radio') {
          const variantId = e.target.dataset.variantId;
          if (variantId) {
            this.swapImages(variantId);
          }
        }
      });
    }

    // Also watch for URL changes (variant in URL)
    this.watchUrlChanges();
    
    // Initial load - check URL for variant
    this.checkInitialVariant();
  }

  checkInitialVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      this.swapImages(variantId);
    }
  }

  watchUrlChanges() {
    // Watch for URL changes via History API
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.onUrlChange();
    };
    
    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.onUrlChange();
    };
    
    window.addEventListener('popstate', () => this.onUrlChange());
  }

  onUrlChange() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId && variantId !== this.currentVariantId) {
      this.swapImages(variantId);
    }
  }

  handleVariantChange(event) {
    const variantId = event.detail?.variantId || event.detail?.id;
    if (variantId) {
      this.swapImages(variantId);
    }
  }

  swapImages(variantId) {
    if (this.currentVariantId === variantId) return;
    this.currentVariantId = variantId;

    const variantData = this.data.variants[variantId];
    if (!variantData) {
      console.warn('VariantGallerySwap: No data for variant', variantId);
      return;
    }

    const images = variantData.images.length > 0 ? variantData.images : this.data.defaultImages;
    
    if (images.length === 0) {
      console.warn('VariantGallerySwap: No images for variant', variantId);
      return;
    }

    this.updateGallery(images);
  }

  updateGallery(images) {
    if (!this.gallery) {
      this.gallery = document.querySelector('media-gallery');
    }
    
    if (!this.gallery) {
      console.warn('VariantGallerySwap: Gallery not found');
      return;
    }

    // Find all image containers in the gallery
    const slideshow = this.gallery.querySelector('slideshow-slides');
    const grid = this.gallery.querySelector('.media-gallery__grid');
    
    if (slideshow) {
      this.updateSlideshow(slideshow, images);
    }
    
    if (grid) {
      this.updateGrid(grid, images);
    }

    // Update thumbnails if present
    const thumbnails = this.gallery.querySelectorAll('.slideshow-controls__thumbnails button, .dialog-thumbnails-list button');
    if (thumbnails.length > 0) {
      this.updateThumbnails(thumbnails, images);
    }

    // Trigger slideshow reset to first slide
    const slideshowComponent = this.gallery.querySelector('slideshow-component');
    if (slideshowComponent && typeof slideshowComponent.goToSlide === 'function') {
      slideshowComponent.goToSlide(0);
    }
  }

  updateSlideshow(container, images) {
    const slides = container.querySelectorAll('slideshow-slide');
    
    slides.forEach((slide, index) => {
      const image = images[index];
      if (!image) {
        slide.style.display = 'none';
        return;
      }
      
      slide.style.display = '';
      const img = slide.querySelector('img');
      if (img) {
        img.src = image.src;
        img.srcset = image.srcset;
        img.alt = image.alt;
        img.dataset.mediaId = image.id;
      }
      
      const mediaDiv = slide.querySelector('.product-media');
      if (mediaDiv) {
        mediaDiv.style.setProperty('--ratio', image.aspectRatio || 1);
        mediaDiv.dataset.mediaId = image.id;
      }
    });

    // Hide extra slides if we have fewer images
    for (let i = images.length; i < slides.length; i++) {
      slides[i].style.display = 'none';
    }
  }

  updateGrid(container, images) {
    const items = container.querySelectorAll('li');
    
    items.forEach((item, index) => {
      const image = images[index];
      if (!image) {
        item.style.display = 'none';
        return;
      }
      
      item.style.display = '';
      const img = item.querySelector('img');
      if (img) {
        img.src = image.src;
        img.srcset = image.srcset;
        img.alt = image.alt;
      }
      
      const mediaDiv = item.querySelector('.product-media');
      if (mediaDiv) {
        mediaDiv.style.setProperty('--ratio', image.aspectRatio || 1);
        mediaDiv.dataset.mediaId = image.id;
      }
      
      // Update aspect ratio on the li element
      item.style.setProperty('--media-preview-ratio', image.aspectRatio || 1);
    });

    // Hide extra items if we have fewer images
    for (let i = images.length; i < items.length; i++) {
      items[i].style.display = 'none';
    }
  }

  updateThumbnails(thumbnails, images) {
    thumbnails.forEach((thumb, index) => {
      const image = images[index];
      if (!image) {
        thumb.style.display = 'none';
        return;
      }
      
      thumb.style.display = '';
      const img = thumb.querySelector('img');
      if (img) {
        img.src = image.src.replace('width=1200', 'width=160');
        img.alt = image.alt;
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Find all product variant data elements and initialize only if they have variant images
  const dataElements = document.querySelectorAll('[id^="variant-images-data-"]');
  dataElements.forEach(el => {
    try {
      const data = JSON.parse(el.textContent);
      if (data.hasVariantImages) {
        const productId = el.id.replace('variant-images-data-', '');
        new VariantGallerySwap(productId);
      }
    } catch (e) {
      console.error('Failed to parse variant data', e);
    }
  });
});

// Also initialize for dynamic content
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    const dataElements = document.querySelectorAll('[id^="variant-images-data-"]');
    dataElements.forEach(el => {
      try {
        const data = JSON.parse(el.textContent);
        if (data.hasVariantImages) {
          const productId = el.id.replace('variant-images-data-', '');
          new VariantGallerySwap(productId);
        }
      } catch (e) {
        console.error('Failed to parse variant data', e);
      }
    });
  });
}
